import React, { useState } from "react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll reach out within 1 business day.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <section className="border-b tf-hairline bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
          <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Contact</div>
          <h1 className="mt-3 font-[Chivo] text-5xl lg:text-6xl font-black text-[#0A2540] tracking-tight">Talk to a licensed advisor.</h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-600">Discovery calls are free. Most engagements start within 48 hours of the initial call.</p>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 rounded-lg border tf-hairline bg-white p-8">
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="contact-name" />
              </div>
              <div>
                <Label htmlFor="email">Work email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="contact-email" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="contact-phone" />
              </div>
              <div>
                <Label>Service of interest</Label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger className="mt-1" data-testid="contact-service">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Individual">Individual Tax Filing</SelectItem>
                    <SelectItem value="Business">Business Tax Filing</SelectItem>
                    <SelectItem value="ITIN">ITIN</SelectItem>
                    <SelectItem value="FBAR/FATCA">FBAR & FATCA</SelectItem>
                    <SelectItem value="IRS Notice">IRS Notice</SelectItem>
                    <SelectItem value="Extensions">Tax Extensions</SelectItem>
                    <SelectItem value="Corporate">Corporate Programs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="message">How can we help?</Label>
              <Textarea id="message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" data-testid="contact-message" />
            </div>
            <Button type="submit" disabled={loading} className="bg-[#0A2540] hover:bg-[#0A2540]/90 h-11" data-testid="contact-submit">
              {loading ? "Sending…" : "Send message"}
            </Button>
          </form>
        </div>

        <aside className="space-y-6">
          <div className="rounded-md border tf-hairline bg-slate-50 p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Direct</div>
            <a href="mailto:info@taxfreefiler.com" className="mt-2 block font-semibold text-[#0A2540] hover:text-[#D32F2F]" data-testid="contact-email">info@taxfreefiler.com</a>
            <a href="tel:+917386724363" className="block text-slate-600 text-sm mt-1 hover:text-[#0A2540]" data-testid="contact-phone">+91 (738) 672-4363</a>
            <a
              href="https://whatsapp.com/channel/0029VbCnQzl4NVilEQdRqq17"
              target="_blank"
              rel="noreferrer noopener"
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-900"
              data-testid="contact-whatsapp"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" /> Follow the Taxfree Filer WhatsApp channel
            </a>
          </div>
          <div className="rounded-md border tf-hairline bg-slate-50 p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Hours</div>
            <div className="mt-2 text-sm text-slate-700">Mon – Sat · 8am – 8pm IST</div>
            <div className="text-sm text-slate-700">Sun · closed (tax season only: 10am – 4pm IST)</div>
          </div>
          <div className="rounded-md border tf-hairline bg-slate-50 p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Address</div>
            <div className="mt-2 text-sm text-slate-700">Taxfree Filer LLP<br />1120 Sixth Avenue, Suite 900<br />New York, NY 10036</div>
          </div>
        </aside>
      </section>
      <Footer />
    </div>
  );
}
